import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PulseHeader } from "@pulselabs-ai/shared-fe-ui";
import {
  updateUser,
  clearUser,
  selectUser,
  selectUserActiveWorkspace,
  setActiveWorkspace,
} from "../../store/slices/userSlice";
import {
  selectWorkspaces,
  setActiveWorkspaceFromId,
  setWorkspaces,
  updateWorkspace,
  Workspace,
} from "../../store/slices/workspaceSlice";
import { getAuthUrl } from "../../utils/authUtils";
import "./PulseAppHeader.scss";
import AuthService from "@pl-flight-recorder/web/services/AuthService";
import { User } from "@pl-flight-recorder/web/interfaces/user";
import { UserWorkspaceStatus } from "@pl-flight-recorder/web/interfaces/UserWorkspaceStatus";

// Update interface to match PulseHeader's expected type
interface PulseUser {
  id: number;
  name: string;
  email: string;
  organization?: {
    id: string;
  };
  status?: string;
  isActive?: boolean;
  picture?: string;
  dp_color?: string;
}

export enum ProductType {
  PULSELABS = "pulse-lab",
  RESEARCH_LABS = "research-lab",
  PRODUCT_LABS = "product-lab",
  PULSE_VOICES = "pulse-voices",
}

// Convert our User type to PulseHeader's User type
const toPulseUser = (user: User): PulseUser => ({
  id: user.id,
  name: user.name,
  email: user.email || "",
  dp_color: user.dp_color || "",
});

const PulseAppHeader: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const workspaces = useSelector(selectWorkspaces);
  const currentUser = useSelector(selectUser);

  const handleLogout = async () => {
    dispatch(clearUser());
    dispatch(setActiveWorkspaceFromId(-1));
    navigate(getAuthUrl() + "/logout");
    return Promise.resolve();
  };

  const handleWorkspaceSwitch = async (workspaceId: number) => {
    try {
      const authService = AuthService.getInstance();
      const response = await authService.switchWorkspace(
        workspaceId,
        currentUser?.token || "",
      );
      console.log("Workspace switch response:", response);

      const workspace = mappedWorkspaces.find(
        (workspace) => workspace.id === workspaceId,
      );

      // Debug log for workspace role
      console.log("Selected workspace:", workspace);
      console.log("Workspace role being set:", workspace?.role?.name);

      if (workspace) {
        dispatch(
          setActiveWorkspace({
            id: workspaceId,
            name: workspace.name,
            userWorkspaceStatus: workspace.userWorkspaceStatus,
            role: { name: workspace.role.name }, // Make sure role is being passed correctly
          }),
        );
        navigate("/");
      }
    } catch (error) {
      console.error("Failed to switch workspace:", error);
    }
  };

  const handleWorkspaceUpdate = async (workspace: {
    id: number;
    name: string;
  }) => {
    try {
      dispatch(
        updateWorkspace({
          ...workspace,
          role: { name: "member" },
          userWorkspaceStatus: UserWorkspaceStatus.PENDING,
          userworkspacemap:
            workspaces.find((w) => w.id === workspace.id)?.userworkspacemap ||
            [],
        }),
      );
    } catch (error) {
      console.error("Failed to update workspace:", error);
    }
  };

  // Load workspaces on component mount
  useEffect(() => {
    const loadWorkspaces = async () => {
      try {
        console.log("currentUser", currentUser);
        if (!currentUser?.organization.id) return;
        const response = await AuthService.getInstance().getWorkspaces(
          currentUser.organization.id,
          currentUser.token || "",
        );
        console.log("workspaces!!", response);
        const allWorkspaces = response.filter((workspace) =>
          workspace.userworkspacemap.some(
            (map) => map.user.id.toString() === currentUser.userId,
          ),
        );
        const userAccessibleWorkspaces = allWorkspaces.map(
          ({ id, name, userworkspacemap }) =>
            ({
              id,
              name,
              userworkspacemap: userworkspacemap,
            }) as unknown as Workspace,
        );
        console.log("userAccessibleWorkspaces", userAccessibleWorkspaces);
        dispatch(setWorkspaces(userAccessibleWorkspaces));
      } catch (error) {
        console.error("Failed to load workspaces:", error);
      }
    };

    loadWorkspaces();
  }, [dispatch, currentUser?.organization.id]);

  interface MappedWorkspace {
    id: number;
    name: string;
    role: { name: string };
    userWorkspaceStatus: UserWorkspaceStatus;
  }

  const mappedWorkspaces = workspaces.map((workspace) => ({
    id: workspace.id,
    name: workspace.name,
    role: {
      name:
        workspace.userworkspacemap?.find(
          (map) => map.user.id.toString() === currentUser?.userId,
        )?.role?.name || "member",
    },
    adminUser: {
      id: workspace.userworkspacemap?.find((map) => map.role?.name === "ADMIN")
        ?.user.id,
      name: workspace.userworkspacemap?.find(
        (map) => map.role?.name === "ADMIN",
      )?.user.name,
    },
    userWorkspaceStatus: (workspace.userworkspacemap?.find(
      (map) => map.user.id.toString() === currentUser?.userId,
    )?.status || UserWorkspaceStatus.PENDING) as UserWorkspaceStatus,
  })) as MappedWorkspace[];
  console.log("workspaces", workspaces);
  console.log("mappedWorkspaces", mappedWorkspaces);

  // Get active workspace from workspace slice
  const mappedSelectedWorkspace = useSelector(selectUserActiveWorkspace);
  console.log("mappedSelectedWorkspace", mappedSelectedWorkspace);

  // And update handleUserUpdate to handle this type
  const handleUserUpdate = (userData: PulseUser) => {
    console.log("handleUserUpdate userData", userData);
    dispatch(
      updateUser({
        ...userData,
        userId: userData.id.toString(),
        organization: userData.organization || { id: "1" },
        picture: userData.picture || "",
        token: "",
      } as User),
    );
  };
  console.log("ROLE", mappedSelectedWorkspace?.role?.name);

  // Add debug logging before render
  useEffect(() => {
    console.log("Current selected workspace:", mappedSelectedWorkspace);
    console.log("Current role:", mappedSelectedWorkspace?.role?.name);
    console.log(
      "Is admin check:",
      mappedSelectedWorkspace?.role?.name === "ADMIN",
    );
  }, [mappedSelectedWorkspace]);

  console.log("currentUser2", currentUser);

  return (
    <PulseHeader
      activeProducts={[]}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      product={ProductType.PRODUCT_LABS as any}
      user={
        currentUser
          ? toPulseUser(currentUser)
          : {
              id: 0,
              name: "",
              email: "",
            }
      }
      authUrl={getAuthUrl()}
      allWorkspaces={mappedWorkspaces}
      // selectedWorkspace={mappedSelectedWorkspace ? {
      //   id: mappedSelectedWorkspace.id,
      //   name: mappedSelectedWorkspace.name,
      // } as Workspace : mappedWorkspaces[0]}
      selectedWorkspace={
        {
          id: mappedSelectedWorkspace?.id || -1,
          name: mappedSelectedWorkspace?.name || "",
          role: mappedSelectedWorkspace?.role || { name: "" },
          userWorkspaceStatus:
            mappedSelectedWorkspace?.userWorkspaceStatus ||
            UserWorkspaceStatus.PENDING,
        } as Workspace
      }
      onLogout={handleLogout}
      switchWorkspace={handleWorkspaceSwitch}
      handleUserUpdate={handleUserUpdate}
      handleWorkspaceUpdate={handleWorkspaceUpdate}
      showWorkspaceSettings={true}
      // showWorkspaceSettings={mappedSelectedWorkspace?.role?.name === "ADMIN"}
    />
  );
};

export default PulseAppHeader;
